import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  notifications: [],
  unreadCount: 0,
  isOpen: false,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (state, action) => {
      const notification = {
        ...action.payload,
        id: action.payload.id || `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: action.payload.timestamp || new Date().toISOString(),
        read: false,
      };
      console.log("📬 Adding notification to state:", notification);
      
      // Check if notification already exists (prevent duplicates)
      const exists = state.notifications.some(n => n.id === notification.id);
      if (exists) {
        console.log("⚠️ Notification already exists, skipping:", notification.id);
        return;
      }
      
      state.notifications.unshift(notification); // Add to beginning
      state.unreadCount += 1;
      
      // Sort by timestamp DESC to ensure newest first
      state.notifications.sort((a, b) => {
        const timeA = new Date(a.timestamp || a.created_at || 0).getTime();
        const timeB = new Date(b.timestamp || b.created_at || 0).getTime();
        return timeB - timeA; // DESC order (newest first)
      });
      
      console.log("📊 Unread count:", state.unreadCount);
      
      // Keep only last 100 notifications (increased from 50)
      if (state.notifications.length > 100) {
        state.notifications = state.notifications.slice(0, 100);
      }
    },
    loadNotificationsFromHistory: (state, action) => {
      console.log("🔄 loadNotificationsFromHistory called with:", {
        payload: action.payload,
        currentState: {
          notificationCount: state.notifications.length,
          unreadCount: state.unreadCount
        }
      });
      
      const { notifications, markAsRead = false, clearExisting = false } = action.payload;
      
      if (!notifications || !Array.isArray(notifications)) {
        console.warn("⚠️ Invalid notifications payload:", notifications);
        return;
      }

      console.log(`📥 Processing ${notifications.length} notifications from history`);

      // If clearExisting is true (on page refresh), clear the state first
      if (clearExisting) {
        console.log("🧹 Clearing existing notifications for fresh load");
        state.notifications = [];
        state.unreadCount = 0;
      }

      // Create a map of existing notification IDs to avoid duplicates
      // Note: If clearExisting is true, this will be empty
      const existingIds = new Set(state.notifications.map(n => n.id));
      console.log(`📋 Existing notification IDs: ${existingIds.size}`);
      
      // Transform API notifications to match Redux format
      console.log("🔄 Starting transformation of", notifications.length, "notifications");
      const transformedNotifications = notifications.map((notif, index) => {
        // Log first notification for debugging
        if (index === 0) {
          console.log("🔄 Transforming first notification:", JSON.stringify(notif, null, 2));
          console.log("🔄 First notification is_read value:", notif.is_read, "type:", typeof notif.is_read);
        }
        
        // Parse data field if it's a string
        let notificationData = {};
        if (notif.data) {
          try {
            notificationData = typeof notif.data === 'string' ? JSON.parse(notif.data) : notif.data;
          } catch (e) {
            console.warn("⚠️ Failed to parse notification data:", e, "for notification:", notif.id);
            notificationData = {};
          }
        }

        // Determine notification type from notification_type or data.type
        let notificationType = "general";
        if (notif.notification_type) {
          if (notif.notification_type.includes("order")) {
            notificationType = "order";
          } else if (notif.notification_type.includes("inventory")) {
            notificationType = "inventory";
          } else if (notif.notification_type.includes("payment")) {
            notificationType = "payment";
          }
        } else if (notificationData.type) {
          if (notificationData.type.includes("order")) {
            notificationType = "order";
          } else if (notificationData.type.includes("inventory")) {
            notificationType = "inventory";
          } else if (notificationData.type.includes("payment")) {
            notificationType = "payment";
          }
        }

        // Determine read status: prioritize database is_read field
        // If markAsRead is true (from action), force read=true
        // Otherwise, use is_read from database (default to false if undefined/null)
        let readStatus = false;
        if (markAsRead) {
          readStatus = true;
        } else if (notif.is_read !== undefined && notif.is_read !== null) {
          // Explicitly check for boolean true
          readStatus = Boolean(notif.is_read);
        } else {
          // If is_read is undefined/null, default to false (unread)
          // This handles cases where the column doesn't exist yet or is NULL
          readStatus = false;
        }

        const transformed = {
          id: notif.id,
          type: notificationType,
          title: notif.title || "Notification",
          message: notif.body || notif.message || "",
          timestamp: notif.created_at || notif.timestamp || new Date().toISOString(),
          read: readStatus, // Use database is_read status
          data: notificationData,
          orderId: notificationData.order_id || notificationData.orderId || null,
          productId: notificationData.product_id || notificationData.productId || null,
        };
        
        // Log first transformed notification
        if (index === 0) {
          console.log("🔄 Transformed first notification:", JSON.stringify(transformed, null, 2));
          console.log("🔄 First notification read status:", transformed.read);
        }
        
        return transformed;
      });
      
      console.log("🔄 Transformed", transformedNotifications.length, "notifications");

      // Filter out duplicates and add new notifications
      const newNotifications = transformedNotifications.filter(
        (notif) => !existingIds.has(notif.id)
      );

      console.log(`✅ Transformed ${transformedNotifications.length} notifications, ${newNotifications.length} are new`);

      // If clearExisting is true, replace all notifications with the new ones
      // Otherwise, merge new notifications with existing ones
      if (clearExisting) {
        console.log("🔄 Clearing existing notifications and replacing with database notifications");
        state.notifications = transformedNotifications; // Use all transformed notifications, not just new ones
      } else {
        // Add new notifications to the beginning, but keep existing ones
        state.notifications = [...newNotifications, ...state.notifications];
      }
      
      // Sort notifications by timestamp DESC (newest first)
      // This ensures notifications are always in descending order (newest first)
      state.notifications.sort((a, b) => {
        const timeA = new Date(a.timestamp || a.created_at || 0).getTime();
        const timeB = new Date(b.timestamp || b.created_at || 0).getTime();
        return timeB - timeA; // DESC order (newest first)
      });
      
      console.log("📊 Sorted notifications by timestamp DESC");
      
      // Update unread count (only count unread notifications)
      state.unreadCount = state.notifications.filter(n => !n.read).length;

      // Keep only last 100 notifications
      if (state.notifications.length > 100) {
        state.notifications = state.notifications.slice(0, 100);
      }

      console.log(`📚 Final state: ${state.notifications.length} total notifications, ${state.unreadCount} unread`);
      if (state.notifications.length > 0) {
        console.log(`📚 First notification (newest):`, state.notifications[0]);
        console.log(`📚 Last notification (oldest):`, state.notifications[state.notifications.length - 1]);
      }
    },
    markAsRead: (state, action) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload
      );
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach((notification) => {
        notification.read = true;
      });
      state.unreadCount = 0;
    },
    removeNotification: (state, action) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload
      );
      if (notification && !notification.read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.notifications = state.notifications.filter(
        (n) => n.id !== action.payload
      );
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    toggleNotificationPanel: (state) => {
      state.isOpen = !state.isOpen;
    },
    setNotificationPanelOpen: (state, action) => {
      state.isOpen = action.payload;
    },
  },
});

export const {
  addNotification,
  loadNotificationsFromHistory,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearAllNotifications,
  toggleNotificationPanel,
  setNotificationPanelOpen,
} = notificationSlice.actions;

export default notificationSlice.reducer;

